import type { SettingsSubComponentProps } from '@carboncopyco/components';
import { SettingsSubKeys } from '@carboncopyco/types/desktop/appSettings';
import { RadioGroup, Slider, TextBase, TextCaption } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useState } from 'react';
import { HeaderBlock } from './HeaderBlock';
import { PersistentStorage } from './PersistentStorage';

const PERSISTENT_STORAGE_LEARN_MORE_LINK =
  'https://help.demostack.com/articles/improve-performance-using-caching';

const AppConfiguration = ({
  saveData,
  getValue,
}: SettingsSubComponentProps) => {
  const [tabCaching, setTabCaching] = useState<number>(
    getValue(SettingsSubKeys.TabCaching)
  );

  const [isPersistentStorageActive, { toggle: toggleActive }] = useBoolean(
    getValue(SettingsSubKeys.PersistentStorageToggleState)
  );

  const CacheMechanismButtons = [
    {
      id: 'persistent',
      label: 'Persistent caching',
      value: 'persistent',
      component: <TextCaption label="Stores data on the local disk" />,
    },
    {
      id: 'volatile',
      label: 'Volatile caching',
      value: 'volatile',
      component: <TextCaption label="Stores data for current session" />,
    },
  ];

  return (
    <div className="flex-col grow">
      <HeaderBlock
        label="Caching"
        description="Store frequently used data in a quick-access place to speed up processes, "
        learnMoreLink={PERSISTENT_STORAGE_LEARN_MORE_LINK}
      />
      <div className="flex flex-col grow pr-7 gap-6">
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col gap-2.5">
            <TextBase
              label="Select caching mechanism"
              color="text-ds-natural-600"
            />
            <RadioGroup
              radios={CacheMechanismButtons}
              onChange={(value) => {
                saveData(
                  SettingsSubKeys.PersistentStorageToggleState,
                  value === 'persistent',
                  toggleActive
                );
              }}
              value={isPersistentStorageActive ? 'persistent' : 'volatile'}
              defaultValue={
                isPersistentStorageActive ? 'persistent' : 'volatile'
              }
            />
          </div>
        </div>
        {!isPersistentStorageActive ? (
          <div className="-mr-7">
            <Slider
              defaultValue={tabCaching}
              onChange={(value) =>
                saveData(SettingsSubKeys.TabCaching, value[0], setTabCaching)
              }
              valueDescription="MB"
              min={0}
              max={100}
              title="Allocate memory size for tab caching"
            />
          </div>
        ) : (
          <PersistentStorage />
        )}
      </div>
    </div>
  );
};

export default AppConfiguration;
